<script setup lang="ts">
import { computed } from "vue";
import { useMounted } from "@vueuse/core";
import { Swiper, SwiperSlide } from "swiper/vue";
import { HomeService } from "~/services/HomeService";
import { store } from "~/store";
import LinkCategory from "@/components/buttons/LinkCategory.vue";

// Swiper modules
import {
  Navigation,
  FreeMode,
  Pagination,
  Scrollbar,
  A11y,
  EffectFade,
} from "swiper/modules";
import useHomeStore from "~/stores/home";

interface HomeCategory {
  id: number;
  name: string;
  links?: {
    self?: string;
  };
  [key: string]: unknown;
}

const homeStore = useHomeStore();
const routeHydrated = useRouteHydrated();
const mounted = useMounted();

// Swiper config
const modules = [Pagination, Scrollbar, A11y, Navigation, EffectFade, FreeMode];

// computed
const categories = useState<HomeCategory[]>("home.categories", () => []);
const rawCategories = useState<unknown>("home.rawCategories", () => undefined);
const categoryList = computed(() =>
  Array.isArray(categories.value) ? categories.value : [],
);
const hasCategories = computed(() => categoryList.value.length > 0);
const fallbackCategories = computed(() => categoryList.value.slice(0, 9));

const categorySeeMore = computed(() => ({
  id: 0,
  name: "Ver todas",
  iconSwiper: "bi-chevron-double-right",
  links: {
    self: "/busqueda-por-categoria",
  },
}));

// lifecycle
const init = async () => {
  if (!routeHydrated.value) {
    const homeService = new HomeService();
    const lsCats = store.getters["catalogs/getDeserealizeCategories"] || [];
    if (import.meta.server || !lsCats || !lsCats.length) {
      await homeService.getCategories();
      const deserializedCategories =
        store.getters["catalogs/getDeserealizeCategories"];
      categories.value = Array.isArray(deserializedCategories)
        ? deserializedCategories
        : [];
      rawCategories.value = store.getters["catalogs/getCategories"];
      homeStore.$patch({
        categories: categories.value,
        rawCategories: rawCategories.value,
      });
    }
  } else {
    store.commit("catalogs/setCategories", rawCategories.value);
  }
};

if (import.meta.server) {
  await init();
} else {
  init(); // en cliente mostramos pantallas de carga
}
</script>

<template>
  <section
    v-if="hasCategories"
    class="mt-4 mt-lg-5 px-3 px-lg-0 explore-by-categories"
  >
    <h2 class="text-secondary title">Explora por categorías</h2>
    <div class="row">
      <div class="col-12 col-lg-11">
        <div
          v-if="!mounted"
          class="categories-fallback d-flex align-items-stretch"
        >
          <div
            v-for="(category, index) in fallbackCategories"
            :key="index"
            class="categories-fallback__item"
          >
            <link-category :category="category" />
          </div>
        </div>
        <swiper
          v-else
          :modules="modules"
          :navigation="false"
          :loop="false"
          :free-mode="true"
          :breakpoints="{
            '320': {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            '640': {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            '768': {
              slidesPerView: 3,
              spaceBetween: 10,
            },
            '992': {
              slidesPerView: 5,
              spaceBetween: 10,
            },
            '1280': {
              slidesPerView: 7,
              spaceBetween: 10,
            },
            '1440': {
              slidesPerView: 9,
              spaceBetween: 10,
            },
          }"
          class="p-0 w-100"
        >
          <swiper-slide
            v-for="(category, index) in categoryList"
            :key="index"
            class="swiper-category"
          >
            <link-category :category="category" />
          </swiper-slide>
        </swiper>
      </div>
      <div class="col-lg-1 d-none d-lg-block">
        <link-category
          class="ms-2 align-self-stretch"
          :style="{ width: '100px !important' }"
          :category="categorySeeMore"
        />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.swiper-category {
  font-size: 16px !important;
}

.categories-fallback {
  overflow-x: auto;
  scroll-snap-type: x proximity;
  gap: 10px;
  -webkit-overflow-scrolling: touch;
}

.categories-fallback__item {
  flex: 0 0 calc(33.3333% - 7px);
  min-width: 0;
  scroll-snap-align: start;
}

@media (min-width: 992px) {
  .categories-fallback__item {
    flex-basis: calc(20% - 8px);
  }
}

@media (min-width: 1280px) {
  .categories-fallback__item {
    flex-basis: calc(14.2857% - 9px);
  }
}
</style>
